'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdEdit,
    MdWarning,
    MdLocalHospital,
    MdDescription,
    MdGavel,
    MdCheckCircle,
    MdCancel
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getGloss,
    submitAppeal,
    approveAppeal,
    rejectAppeal,
    Gloss,
    GLOSS_STATUS_LABELS,
    GLOSS_STATUS_COLORS,
    GLOSS_TYPE_LABELS,
} from '@/lib/api/billing/gloss-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function GlossDetailPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canUpdate } = useResourcePermissions('billing', 'glosses')

    const [gloss, setGloss] = useState<Gloss | null>(null)
    const [loading, setLoading] = useState(true)
    const [showAppealModal, setShowAppealModal] = useState(false)
    const [showRejectModal, setShowRejectModal] = useState(false)
    const [appealText, setAppealText] = useState('')
    const [rejectReason, setRejectReason] = useState('')
    const [actionLoading, setActionLoading] = useState(false)

    useEffect(() => {
        if (id && user?.companyId) {
            loadGloss()
        }
    }, [id, user?.companyId])

    const loadGloss = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getGloss(parseInt(id, 10))
            if (response.success && response.result) {
                setGloss(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar glosa')
                navigate('/billing/glosses')
            }
        } catch (error) {
            toast.error('Erro ao carregar glosa')
            navigate('/billing/glosses')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmitAppeal = async () => {
        if (!id || !appealText.trim()) {
            toast.error('Informe o texto do recurso')
            return
        }

        setActionLoading(true)
        try {
            const response = await submitAppeal(parseInt(id, 10), appealText)
            if (response.success) {
                toast.success('Recurso enviado com sucesso')
                setShowAppealModal(false)
                setAppealText('')
                loadGloss()
            } else {
                toast.error(response.error || 'Erro ao enviar recurso')
            }
        } catch (error) {
            toast.error('Erro ao enviar recurso')
        } finally {
            setActionLoading(false)
        }
    }

    const handleApproveAppeal = async () => {
        if (!id || !gloss?.appeals || gloss.appeals.length === 0) return

        const appealId = gloss.appeals[0].id
        setActionLoading(true)
        try {
            const response = await approveAppeal(parseInt(id, 10), appealId)
            if (response.success) {
                toast.success('Recurso aprovado - glosa recuperada')
                loadGloss()
            } else {
                toast.error(response.error || 'Erro ao aprovar recurso')
            }
        } catch (error) {
            toast.error('Erro ao aprovar recurso')
        } finally {
            setActionLoading(false)
        }
    }

    const handleRejectAppeal = async () => {
        if (!id || !gloss?.appeals || gloss.appeals.length === 0) return

        const appealId = gloss.appeals[0].id
        setActionLoading(true)
        try {
            const response = await rejectAppeal(
                parseInt(id, 10),
                appealId,
                rejectReason || undefined
            )
            if (response.success) {
                toast.success('Recurso rejeitado')
                setShowRejectModal(false)
                setRejectReason('')
                loadGloss()
            } else {
                toast.error(response.error || 'Erro ao rejeitar recurso')
            }
        } catch (error) {
            toast.error('Erro ao rejeitar recurso')
        } finally {
            setActionLoading(false)
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando glosa...
                </p>
            </div>
        )
    }

    if (!gloss) {
        return null
    }

    const canAppeal = gloss.status === 'GLOSS_PENDING'
    const canDecideAppeal = gloss.status === 'GLOSS_APPEALED'
    const canEditGloss = gloss.status === 'GLOSS_PENDING'

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing/glosses')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                        <MdWarning className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Glosa #{gloss.id}
                        </h1>
                        <div className="flex items-center gap-2 mt-1">
                            <span
                                className={`badge ${GLOSS_STATUS_COLORS[gloss.status]}`}
                            >
                                {GLOSS_STATUS_LABELS[gloss.status]}
                            </span>
                            <span className="badge badge-ghost badge-sm">
                                {GLOSS_TYPE_LABELS[gloss.type as keyof typeof GLOSS_TYPE_LABELS]}
                            </span>
                        </div>
                    </div>
                </div>
                {canUpdate && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {canAppeal && (
                            <button
                                onClick={() => setShowAppealModal(true)}
                                className="btn btn-info btn-sm gap-2"
                            >
                                <MdGavel className="text-lg" />
                                Recursar
                            </button>
                        )}
                        {canDecideAppeal && (
                            <>
                                <button
                                    onClick={handleApproveAppeal}
                                    className="btn btn-success btn-sm gap-2"
                                    disabled={actionLoading}
                                >
                                    <MdCheckCircle className="text-lg" />
                                    Aceitar Recurso
                                </button>
                                <button
                                    onClick={() => setShowRejectModal(true)}
                                    className="btn btn-error btn-sm gap-2"
                                    disabled={actionLoading}
                                >
                                    <MdCancel className="text-lg" />
                                    Rejeitar Recurso
                                </button>
                            </>
                        )}
                        {canEditGloss && (
                            <button
                                onClick={() =>
                                    navigate(`/billing/glosses/${id}/edit`)
                                }
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdEdit className="text-lg" />
                                Editar
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Value Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">
                            Valor Original
                        </span>
                        <p className="text-xl font-bold">
                            {formatCurrency(gloss.originalValue)}
                        </p>
                    </div>
                </div>
                <div className="card bg-error/10 border border-error/30 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-error uppercase">
                            Valor Glosado
                        </span>
                        <p className="text-xl font-bold text-error">
                            {formatCurrency(gloss.glossValue)}
                        </p>
                    </div>
                </div>
                <div
                    className={`card ${gloss.recoveredValue ? 'bg-success/10 border-success/30' : 'bg-base-100 border-base-300/50'} border shadow-sm`}
                >
                    <div className="card-body p-4">
                        <span
                            className={`text-xs uppercase ${gloss.recoveredValue ? 'text-success' : 'text-base-content/60'}`}
                        >
                            Valor Recuperado
                        </span>
                        <p
                            className={`text-xl font-bold ${gloss.recoveredValue ? 'text-success' : ''}`}
                        >
                            {formatCurrency(gloss.recoveredValue || 0)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <div className="flex items-center gap-2 mb-4">
                            <MdLocalHospital className="text-secondary text-xl" />
                            <h2 className="card-title text-lg">Convenio</h2>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <p className="text-xs text-base-content/60 uppercase">
                                    Nome
                                </p>
                                <p className="font-semibold">
                                    {gloss.healthPlan?.name || '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-base-content/60 uppercase">
                                    Data da Glosa
                                </p>
                                <p className="font-semibold">
                                    {gloss.createdAt
                                        ? format(
                                            new Date(gloss.createdAt),
                                            'dd/MM/yyyy',
                                            { locale: ptBR }
                                        )
                                        : '-'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <div className="flex items-center gap-2 mb-4">
                            <MdDescription className="text-secondary text-xl" />
                            <h2 className="card-title text-lg">Guia</h2>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <p className="text-xs text-base-content/60 uppercase">
                                    Número
                                </p>
                                <p className="font-semibold">
                                    {gloss.guide?.guideNumber || '-'}
                                </p>
                            </div>
                            {gloss.item?.procedureCode && (
                                <div>
                                    <p className="text-xs text-base-content/60 uppercase">
                                        Procedimento
                                    </p>
                                    <p className="font-semibold">
                                        {gloss.item.procedureCode} -{' '}
                                        {gloss.item.procedureName}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Gloss Details */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">
                        Detalhes da Glosa
                    </h2>
                    <div className="space-y-4">
                        {gloss.glossCode && (
                            <div>
                                <p className="text-xs text-base-content/60 uppercase">
                                    Código da Glosa
                                </p>
                                <p className="font-semibold font-mono">
                                    {gloss.glossCode}
                                </p>
                            </div>
                        )}
                        <div>
                            <p className="text-xs text-base-content/60 uppercase mb-1">
                                Motivo da Glosa
                            </p>
                            <p className="p-3 bg-base-200/50 rounded-lg">
                                {gloss.glossDescription}
                            </p>
                        </div>
                        {gloss.notes && (
                            <div>
                                <p className="text-xs text-base-content/60 uppercase mb-1">
                                    Observações
                                </p>
                                <p className="p-3 bg-base-200/50 rounded-lg">
                                    {gloss.notes}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Appeal Info */}
            {gloss.appeals && gloss.appeals.length > 0 && (
                <div className="card bg-info/10 border border-info/30 shadow-sm">
                    <div className="card-body">
                        <div className="flex items-center gap-2 mb-4">
                            <MdGavel className="text-info text-xl" />
                            <h2 className="card-title text-lg">
                                Recursos Apresentados
                            </h2>
                        </div>
                        {gloss.appeals.map((appeal: any) => (
                            <div key={appeal.id} className="mb-4 last:mb-0">
                                <p className="p-3 bg-base-100 rounded-lg">
                                    {appeal.reason}
                                </p>
                                {appeal.appealDate && (
                                    <p className="text-sm text-base-content/60 mt-2">
                                        Data do recurso:{' '}
                                        {format(
                                            new Date(appeal.appealDate),
                                            'dd/MM/yyyy',
                                            { locale: ptBR }
                                        )}
                                    </p>
                                )}
                                {appeal.responseDescription && (
                                    <div className="mt-4">
                                        <p className="text-xs text-base-content/60 uppercase mb-1">
                                            Resposta ao Recurso
                                        </p>
                                        <p className="p-3 bg-base-100 rounded-lg">
                                            {appeal.responseDescription}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Appeal Modal */}
            {showAppealModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Enviar Recurso</h3>
                        <div className="py-4">
                            <label className="label">
                                <span className="label-text">
                                    Justificativa do Recurso{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                value={appealText}
                                onChange={e => setAppealText(e.target.value)}
                                placeholder="Descreva a justificativa para o recurso..."
                                rows={5}
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                onClick={() => {
                                    setShowAppealModal(false)
                                    setAppealText('')
                                }}
                                className="btn btn-ghost"
                                disabled={actionLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSubmitAppeal}
                                className="btn btn-info"
                                disabled={actionLoading || !appealText.trim()}
                            >
                                {actionLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Enviar Recurso'
                                )}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowAppealModal(false)}
                    ></div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Rejeitar Recurso</h3>
                        <div className="py-4">
                            <label className="label">
                                <span className="label-text">
                                    Motivo da Rejeição
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                value={rejectReason}
                                onChange={e => setRejectReason(e.target.value)}
                                placeholder="Informe o motivo (opcional)"
                                rows={3}
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                onClick={() => {
                                    setShowRejectModal(false)
                                    setRejectReason('')
                                }}
                                className="btn btn-ghost"
                                disabled={actionLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleRejectAppeal}
                                className="btn btn-error"
                                disabled={actionLoading}
                            >
                                {actionLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Rejeitar'
                                )}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowRejectModal(false)}
                    ></div>
                </div>
            )}
        </div>
    )
}
